<template>
  <Teleport to="body">
    <!-- Overlay con difuminado mejorado -->
    <div class="modal-overlay" @click="$emit('close')">
      <div class="modal-content" @click.stop>

        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div class="flex-1 min-w-0 mr-3">
            <h3 class="text-lg font-semibold text-gray-900">Asignar residente</h3>
            <p class="text-sm text-gray-500 truncate" :title="item.fileName">{{ item.fileName }}</p>
          </div>
          <span @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 text-3xl font-light cursor-pointer select-none transition">
            &times;
          </span>
        </div>

        <!-- Body - Layout horizontal -->
        <div class="flex flex-col md:flex-row p-4 gap-6">

          <!-- Columna izquierda: Imagen y asignación actual -->
          <div class="md:w-1/2 flex flex-col items-center">
            <!-- Preview de imagen -->
            <div v-if="item.previewUrl" class="w-full flex justify-center mb-3">
              <img :src="item.previewUrl" :alt="item.fileName"
                class="max-w-full max-h-64 object-contain rounded-lg border border-gray-200 shadow-sm" />
            </div>

            <!-- Info actual compacta -->
            <div v-if="item.residentName" class="w-full text-center text-sm text-gray-600">
              <span class="text-gray-500">Asignado a:</span>
              <span class="font-medium text-gray-900 ml-1">{{ item.residentName }}</span>
              <span v-if="item.residentAddress" class="block text-xs text-gray-500">{{ item.residentAddress }}</span>
            </div>
            <div v-else class="w-full text-center">
              <span class="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">Sin asignar</span>
            </div>
          </div>

          <!-- Columna derecha: Buscador y resultados -->
          <div class="md:w-1/2 flex flex-col">
            <!-- Buscador -->
            <div class="mb-3">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Buscar residente
              </label>
              <div class="relative">
                <input v-model="searchQuery" type="text" placeholder="Nombre o dirección..."
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  @input="onSearchInput" autofocus />
                <div class="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg v-if="searching" class="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  <svg v-else class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Resultados -->
            <div v-if="searchResults.length" class="flex-1">
              <p class="text-xs text-gray-500 mb-2">
                {{ searchResults.length }} resultado{{ searchResults.length !== 1 ? 's' : '' }}
              </p>
              <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                <button v-for="resident in searchResults" :key="resident.value" @click="selectResident(resident)"
                  type="button" class="w-full text-left p-2.5 border rounded-lg transition-all text-sm hover:bg-gray-50"
                  :class="selectedResident?.value === resident.value
                    ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200'
                    : 'border-gray-200'">
                  <p class="font-medium text-gray-900">{{ resident.residentName }}</p>
                  <p v-if="resident.residentAddress" class="text-xs text-gray-600 truncate mt-0.5">
                    {{ resident.residentAddress }}
                  </p>
                </button>
              </div>
            </div>

            <div v-else-if="searchQuery && searchQuery.length >= 2 && !searching"
              class="flex-1 flex items-center justify-center text-gray-500">
              <div class="text-center py-4">
                <svg class="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-sm">No se encontraron residentes</p>
              </div>
            </div>

            <div v-else class="flex-1 flex items-center justify-center text-gray-400">
              <p class="text-sm">Escribe para buscar...</p>
            </div>

            <!-- Selección confirmada -->
            <div v-if="selectedResident" class="mt-3 bg-green-50 border border-green-200 rounded-lg p-2.5">
              <p class="text-xs text-green-600">
                <span class="font-medium">Seleccionado:</span>
                <span class="text-green-800 ml-1">{{ selectedResident.residentName }}</span>
                <span v-if="selectedResident.residentAddress" class="text-green-700"> — {{
                  selectedResident.residentAddress }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button @click="$emit('close')" :disabled="busy" type="button"
            class="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors">
            Cancelar
          </button>
          <button @click="submit" :disabled="!selectedResident || busy" type="button"
            class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <span v-if="busy" class="flex items-center gap-2">
              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
              </svg>
              Asignando...
            </span>
            <span v-else>Asignar</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, watch } from 'vue'

export default {
  name: 'ReassignModal',
  props: {
    item: { type: Object, required: true },
    busy: { type: Boolean, default: false },
    findResidents: { type: Function, required: true }
  },
  emits: ['close', 'submit'],
  setup(props, { emit }) {
    const searchQuery = ref('')
    const searchResults = ref([])
    const selectedResident = ref(null)
    const searching = ref(false)
    let searchTimeout = null

    async function onSearchInput() {
      if (searchTimeout) clearTimeout(searchTimeout)

      const query = searchQuery.value.trim()
      if (query.length < 2) {
        searchResults.value = []
        return
      }

      searchTimeout = setTimeout(async () => {
        searching.value = true
        try {
          searchResults.value = await props.findResidents(query)
        } catch (e) {
          console.error('Error buscando:', e)
          searchResults.value = []
        } finally {
          searching.value = false
        }
      }, 300)
    }

    function selectResident(resident) {
      selectedResident.value = resident
    }

    function submit() {
      if (!selectedResident.value) return

      emit('submit', {
        subjectType: 'RESIDENT',
        subjectUuid: selectedResident.value.value,
        residentData: {
          residentName: selectedResident.value.residentName,
          residentAddress: selectedResident.value.residentAddress
        }
      })
    }

    watch(() => props.item, (newItem) => {
      if (newItem?.residentName) {
        searchQuery.value = newItem.residentName
        onSearchInput()
      }
    }, { immediate: true })

    return {
      searchQuery,
      searchResults,
      selectedResident,
      searching,
      onSearchInput,
      selectResident,
      submit
    }
  }
}
</script>

<style scoped>
/* Overlay del modal con difuminado */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.50);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Contenedor del modal con animación */
.modal-content {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 48rem;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px) scale(0.95);
    opacity: 0;
  }

  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* Scrollbar personalizado para resultados */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>